import { HeroMinimal, BookingCTA } from "../components/sections";
import {
  SectionHeader,
  FeatureCard,
  Divider,
  CTA,
} from "../components/sections";
import {
  Target,
  Users,
  Brain,
  Shield,
  Send,
  CheckCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

const solutionAreas = [
  {
    title: "Sales Strategy",
    description:
      "Foundation-first approach to outbound. We define the system before we execute.",
    icon: Target,
    components: [
      "ICP definition & validation",
      "Micro-segmentation strategy",
      "Buyer committee mapping",
      "Messaging direction & frameworks",
      "Market prioritization & sizing",
      "Competitive positioning",
      "Success metrics & KPI design",
    ],
  },
  {
    title: "Prospecting",
    description:
      "Multi-source discovery, enrichment, and verification provide the fuel for the engine.",
    icon: Users,
    components: [
      "Company discovery & identification",
      "Contact discovery & mapping",
      "Lead sourcing from multiple providers",
      "Automated company research",
      "Multi-source data enrichment",
      "Real-time contact verification",
      "Data quality scoring & monitoring",
    ],
  },
  {
    title: "AI & Automation",
    description:
      "Intelligence and workflow automation that multiplies your sales capacity.",
    icon: Brain,
    components: [
      "AI-powered prospect research",
      "Company intelligence & signals",
      "Automated classification & scoring",
      "Dynamic personalization engine",
      "AI message generation & variation",
      "Workflow automation & branching",
      "Predictive optimization",
    ],
  },
  {
    title: "Outbound Execution",
    description:
      "Multi-channel outreach with smart sequences, follow-ups, and response handling.",
    icon: Send,
    components: [
      "Campaign architecture & design",
      "Multi-touch sequence orchestration",
      "Dynamic personalization at scale",
      "Smart follow-up logic",
      "Response classification & routing",
      "CRM synchronization",
      "Sales team notifications",
    ],
  },
  {
    title: "Infrastructure",
    description:
      "The technical foundation that makes high-volume outbound reliable and scalable.",
    icon: Shield,
    components: [
      "Dedicated domain strategy",
      "Inbox pool management & warm-up",
      "Authentication (SPF/DKIM/DMARC/BIMI)",
      "Deliverability monitoring & optimization",
      "Distributed sending architecture",
      "Human-like sending patterns",
      "Bounce & complaint management",
    ],
  },
  {
    title: "Optimization",
    description: "Closed-loop improvement: analyze, learn, refine, scale.",
    icon: RefreshCw,
    components: [
      "Performance analytics & cohort analysis",
      "A/B test design & statistical analysis",
      "Qualification model refinement",
      "Segmentation iteration",
      "Sequence optimization",
      "Attribution & ROI reporting",
      "Strategic scaling recommendations",
    ],
  },
];

const engagementModels = [
  {
    title: "Complete Outbound Engine",
    description:
      "End-to-end system: strategy through optimization. For companies wanting a fully managed outbound capability.",
    features: [
      "All 6 solution areas included",
      "Dedicated system architect",
      "Weekly optimization reviews",
      "Monthly strategy sessions",
      "Full CRM integration",
      "Custom reporting dashboard",
    ],
    bestFor:
      "Series B+ SaaS, established sales teams, aggressive growth targets",
  },
  {
    title: "Prospecting + Data Foundation",
    description:
      "Build the data layer: discovery, enrichment, verification. Your team executes outreach.",
    features: [
      "ICP definition & segmentation",
      "Company & contact discovery",
      "Multi-source enrichment",
      "Verification & quality scoring",
      "CRM-ready data delivery",
      "Monthly data refreshes",
    ],
    bestFor: "Teams with strong outbound execution, need better fuel",
  },
  {
    title: "AI & Automation Layer",
    description:
      "Add intelligence to your existing stack: research, personalization, qualification, workflow automation.",
    features: [
      "AI research integration",
      "Personalization engine",
      "Qualification scoring",
      "Workflow automation",
      "CRM workflow builder",
      "Model training & updates",
    ],
    bestFor: "Teams with data & infrastructure, need leverage",
  },
  {
    title: "Infrastructure Setup & Audit",
    description:
      "Technical foundation: domains, inboxes, authentication, deliverability, monitoring.",
    features: [
      "Domain & subdomain configuration",
      "Inbox pool setup & warm-up",
      "Full authentication stack",
      "Deliverability audit & remediation",
      "Monitoring & alerting setup",
      "Best practice documentation",
    ],
    bestFor: "Companies scaling outbound, hitting deliverability walls",
  },
];

function SolutionsPage() {
  return (
    <>
      <HeroMinimal
        title="One Outbound Engine. Built Around Your Sales Process."
        subtitle="Engagements can be comprehensive while components can be modular depending on your company's needs. Choose the solution areas that create the most leverage."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See How It Works"
        secondaryCtaHref="/how-it-works"
      />

      {/* Solution Areas */}
      <section className="section" aria-labelledby="areas-title">
        <div className="container">
          <SectionHeader
            id="areas-title"
            eyebrow="Solution Areas"
            title="Six Capabilities. One System."
            description="Each area can be engaged independently or as part of the complete engine. They share data, intelligence, and infrastructure."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {solutionAreas.map((area, index) => (
              <FeatureCard
                key={area.title}
                icon={<area.icon size={24} className="text-accent" />}
                title={area.title}
                description={area.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              />
            ))}
          </div>

          {/* Expandable details */}
          <div className="max-w-5xl mx-auto mt-16 space-y-8">
            {solutionAreas.map((area, index) => (
              <details
                key={area.title}
                className="group card p-6 animate-slide-up"
                style={{ animationDelay: `${(index + 6) * 50}ms` }}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center gap-3">
                    <area.icon
                      size={24}
                      className="text-accent"
                      aria-hidden="true"
                    />
                    <h3 className="h3 text-text-primary">{area.title}</h3>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-text-muted group-open:rotate-90 transition-transform"
                    aria-hidden="true"
                  />
                </summary>
                <div className="mt-6 pt-6 border-t border-border animate-slide-down">
                  <ul className="grid md:grid-cols-2 gap-3" role="list">
                    {area.components.map((component, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-text-secondary"
                      >
                        <CheckCircle
                          size={14}
                          className="text-accent flex-shrink-0"
                          aria-hidden="true"
                        />
                        {component}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="models-title"
      >
        <div className="container">
          <SectionHeader
            id="models-title"
            eyebrow="Engagement Models"
            title="Choose Your Starting Point"
            description="Every outbound system is built around your market, ICP, sales goals, target volume, infrastructure, and required level of automation."
            align="center"
          />
          <div className="grid md:grid-cols-2 gap-8 mt-12 max-w-5xl mx-auto">
            {engagementModels.map((model, index) => (
              <div
                key={model.title}
                className="card-elevated p-8 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h2 text-text-primary mb-2">{model.title}</h3>
                <p className="body text-text-secondary mb-6">
                  {model.description}
                </p>
                <div className="mb-6">
                  <h4 className="label text-text-primary mb-3">Includes</h4>
                  <ul className="space-y-2" role="list">
                    {model.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-text-secondary"
                      >
                        <CheckCircle
                          size={14}
                          className="text-accent flex-shrink-0"
                          aria-hidden="true"
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-4 bg-surface rounded-lg border border-border">
                  <h4 className="label text-text-primary mb-2">Best For</h4>
                  <p className="text-sm text-text-secondary">{model.bestFor}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modular Note */}
      <section className="section" aria-labelledby="modular-title">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Divider />
            <h2
              id="modular-title"
              className="h1 md:display-sm text-text-primary mb-4"
            >
              Need the complete engine or only a specific component?
            </h2>
            <p className="body-lg text-text-secondary mb-8">
              Let's determine what makes sense for your sales process. No
              pressure, just a clear assessment of where leverage exists.
            </p>
            <CTA
              primaryText="Book a Strategy Call"
              primaryHref="/book"
              secondaryText="View Technology"
              secondaryHref="/technology/ai-automation"
              align="center"
            />
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default SolutionsPage;
